/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Injectable } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import Swal from 'sweetalert2';

const CLOSE_BUTTON_HTML = '<svg class="svg" role="img" aria-hidden="true"><use xlink:href="assets/sprite/sprite.svg#close"></use></svg>';

@Injectable()
export class DialogService {
  showConfirmDialog(content: string | HTMLElement, translocoService: TranslocoService, config?: ConfirmDialogConfig) {
    if (!config) {
      config = {};
    }
    return this.popupWithFocusedConfirmButton()
      .fire({
        text: content instanceof HTMLElement ? undefined : content,
        html: content instanceof HTMLElement ? content : undefined,
        icon: undefined,
        showCancelButton: true,
        reverseButtons: true,
        allowOutsideClick: false,
        closeButtonHtml: CLOSE_BUTTON_HTML,
        showCloseButton: config.showCloseButton !== undefined && config.showCloseButton !== null ? config.showCloseButton : true,
        customClass: { popup: 'new-version f_inside-dialog' },
        cancelButtonText: config.cancelBtnText ? config.cancelBtnText : translocoService.translate('translations.global.button.cancel'),
        confirmButtonText: config.confirmBtnText ? config.confirmBtnText : translocoService.translate('translations.global.button.confirm'),
      })
      .then((result) => {
        // Return true for confirm, false for cancel, null/undefined for close (X)
        if (result.isConfirmed) {
          return true;
        } else if (result.isDismissed && result.dismiss === Swal.DismissReason.cancel) {
          return false;
        }
        return null;
      });
  }

  /** Single-button informational popup; resolves once the user dismisses it. */
  showInfoDialog(content: string | HTMLElement, translocoService: TranslocoService, config: InfoDialogConfig = {}): Promise<void> {
    return this.popupWithFocusedConfirmButton()
      .fire({
        title: config.title,
        text: content instanceof HTMLElement ? undefined : content,
        html: content instanceof HTMLElement ? content : undefined,
        icon: undefined,
        showCancelButton: false,
        allowOutsideClick: false,
        closeButtonHtml: CLOSE_BUTTON_HTML,
        showCloseButton: config.showCloseButton ?? true,
        customClass: { popup: 'new-version f_inside-dialog' },
        confirmButtonText: config.confirmBtnText ?? translocoService.translate('translations.global.button.ok'),
      })
      .then(() => undefined);
  }

  private popupWithFocusedConfirmButton() {
    return Swal.mixin({
      didOpen: (modal) => {
        const confirmBtn = modal.querySelector('.swal2-confirm') as HTMLInputElement | HTMLSelectElement;
        if (confirmBtn) {
          setTimeout(() => {
            confirmBtn.focus();
          }, 0);
        }
      },
    });
  }
}

export interface ConfirmDialogConfig {
  hideIcon?: boolean;
  showCloseButton?: boolean;
  confirmBtnText?: string;
  cancelBtnText?: string;
}

export interface InfoDialogConfig {
  title?: string;
  showCloseButton?: boolean;
  confirmBtnText?: string;
}
